import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Buildings, Plus, X } from 'phosphor-react';
import {
  centralApi,
  IEmpresaCarteira,
  identificacaoDaEmpresa,
  podeBuscarEmpresa,
} from '../../hooks/useCentralPermissoes';
import { AZUL, Busca, Contador, Eyebrow, MONO, TEXTO_SUAVE } from './ui';

/**
 * Empresas liberadas uma a uma, além dos grupos de empresas da carteira (somam).
 * `herdadas`: as que já vêm do grupo (só para mostrar, não dá para tirar aqui).
 */
export function EmpresasAvulsas({
  selecionadas,
  onChange,
  herdadas = [],
  desabilitado,
}: {
  selecionadas: IEmpresaCarteira[];
  onChange: (empresas: IEmpresaCarteira[]) => void;
  herdadas?: IEmpresaCarteira[];
  desabilitado?: boolean;
}) {
  const [digitado, setDigitado] = useState('');
  const [busca, setBusca] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusca(digitado.trim()), 300);
    return () => clearTimeout(t);
  }, [digitado]);

  const { data: achadas = [], isFetching } = useQuery({
    queryKey: ['central', 'busca-empresas', busca],
    queryFn: () => centralApi.buscarEmpresas(busca),
    enabled: podeBuscarEmpresa(busca),
  });

  const ids = new Set([...selecionadas, ...herdadas].map(e => e.id));
  const candidatas = achadas.filter(e => !ids.has(e.id)).slice(0, 8);

  const chip = (e: IEmpresaCarteira, origem: 'avulsa' | 'grupo') => (
    <Flex
      key={`${origem}-${e.id}`}
      align="center"
      gap="8px"
      h="34px"
      pl="12px"
      pr={origem === 'avulsa' && !desabilitado ? '6px' : '12px'}
      borderRadius="999px"
      fontSize="13px"
      fontWeight={600}
      bg={origem === 'avulsa' ? '#DCFCE7' : '#DFF3FF'}
      color={origem === 'avulsa' ? '#166534' : '#075985'}
      boxShadow={origem === 'avulsa' ? 'inset 0 0 0 1px rgba(22,101,52,.2)' : undefined}
      maxW="100%"
    >
      <Buildings size={14} />
      <Text noOfLines={1}>{e.nome}</Text>
      <Text as="span" fontFamily={MONO} fontSize="11px" fontWeight={500} opacity={0.8}>
        {identificacaoDaEmpresa(e)}
      </Text>
      {origem === 'grupo' && (
        <Text as="span" fontSize="11px" fontWeight={500} opacity={0.85}>
          do grupo
        </Text>
      )}
      {origem === 'avulsa' && !desabilitado && (
        <Box
          as="button"
          type="button"
          aria-label={`Tirar ${e.nome}`}
          onClick={() => onChange(selecionadas.filter(s => s.id !== e.id))}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w="22px"
          h="22px"
          borderRadius="50%"
          _hover={{ bg: 'rgba(22,101,52,.12)' }}
        >
          <X size={12} />
        </Box>
      )}
    </Flex>
  );

  return (
    <Flex direction="column" gap="10px">
      <Flex justify="space-between" align="center" gap="10px" wrap="wrap">
        <Eyebrow>EMPRESAS AVULSAS</Eyebrow>
        <Contador>{selecionadas.length}</Contador>
      </Flex>
      <Text fontSize="12.5px" color={TEXTO_SUAVE}>
        Empresas liberadas uma a uma. Somam aos grupos de empresas: quem tem só empresas avulsas vê
        só elas, em todas as telas do sistema.
      </Text>

      {!desabilitado && (
        <Box>
          <Box maxW="420px">
            <Busca
              valor={digitado}
              onChange={setDigitado}
              placeholder="Buscar empresa por nome, CNPJ ou ID"
            />
          </Box>
          {podeBuscarEmpresa(busca) && (
            <Flex direction="column" gap="4px" mt="8px">
              {isFetching && <Spinner size="sm" color={AZUL} />}
              {candidatas.map(e => (
                <Flex
                  key={e.id}
                  align="center"
                  gap="10px"
                  p="8px 10px"
                  borderRadius="12px"
                  bg="white"
                  boxShadow="inset 0 0 0 1px rgba(15,23,42,.06)"
                  wrap="wrap"
                >
                  <Box flex="1 1 240px" minW={0}>
                    <Text fontSize="13px" fontWeight={600} noOfLines={1}>
                      {e.nome}
                    </Text>
                    <Text fontSize="12px" color={TEXTO_SUAVE}>
                      {identificacaoDaEmpresa(e)}
                      {e.grupo_de_empresas ? ` · grupo ${e.grupo_de_empresas}` : ''}
                    </Text>
                  </Box>
                  <Button
                    size="xs"
                    variant="outline"
                    leftIcon={<Plus size={12} />}
                    onClick={() => {
                      onChange([...selecionadas, e]);
                      setDigitado('');
                    }}
                  >
                    Liberar empresa
                  </Button>
                </Flex>
              ))}
              {!isFetching && candidatas.length === 0 && (
                <Text fontSize="12.5px" color={TEXTO_SUAVE}>
                  Nenhuma empresa nova com esse nome, CNPJ ou ID.
                </Text>
              )}
            </Flex>
          )}
        </Box>
      )}

      <Flex wrap="wrap" gap="8px">
        {selecionadas.map(e => chip(e, 'avulsa'))}
        {herdadas.filter(h => !selecionadas.some(s => s.id === h.id)).map(e => chip(e, 'grupo'))}
        {selecionadas.length === 0 && herdadas.length === 0 && (
          <Text fontSize="13px" color={TEXTO_SUAVE}>
            Nenhuma empresa avulsa liberada.
          </Text>
        )}
      </Flex>
    </Flex>
  );
}
